import type { AgencyVisibility } from '@saaga/shared/interfaces/agency.interface'
import { ArrowLeftIcon, ChevronDownIcon, Loader2Icon, SendIcon, UploadIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'

import { AgencyVisibilityBadge } from './agency-visibility-badge.atom'

const isPublishDropdownVisible = (visibility: AgencyVisibility): boolean =>
  visibility === 'draft' || visibility === 'private'

interface PublishDropdownProps {
  visibility: AgencyVisibility
  isLoading: boolean
  onPublishToOrganization: () => void
  onSubmitForReview: () => void
}

const PublishDropdown = ({
  visibility,
  isLoading,
  onPublishToOrganization,
  onSubmitForReview,
}: PublishDropdownProps): ReactNode => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button type="button" className="agency-form-publish-trigger gap-2" disabled={isLoading}>
        <UploadIcon className="size-4" />
        Publish
        <ChevronDownIcon className="size-3" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end">
      {visibility === 'draft' && (
        <DropdownMenuItem
          className="agency-form-publish-org-item"
          onClick={onPublishToOrganization}
        >
          <UploadIcon className="mr-2 size-4" />
          Publish to Organization
        </DropdownMenuItem>
      )}
      {visibility === 'private' && (
        <DropdownMenuItem className="agency-form-submit-review-item" onClick={onSubmitForReview}>
          <SendIcon className="mr-2 size-4" />
          Submit for Public Review
        </DropdownMenuItem>
      )}
    </DropdownMenuContent>
  </DropdownMenu>
)

interface AgencyFormHeaderActionsProps {
  visibility: AgencyVisibility
  version: number
  isLoading: boolean
  isSaveDisabled: boolean
  onSaveDraft: () => void
  onPublishToOrganization: () => void
  onSubmitForReview: () => void
}

const AgencyFormHeaderActions = ({
  visibility,
  version,
  isLoading,
  isSaveDisabled,
  onSaveDraft,
  onPublishToOrganization,
  onSubmitForReview,
}: AgencyFormHeaderActionsProps): ReactNode => (
  <div className="agency-form-header-right flex items-center gap-3">
    <div className="agency-form-badges flex items-center gap-2">
      <AgencyVisibilityBadge visibility={visibility} />
      <Badge variant="outline" className="agency-form-version-badge">
        v{version}
      </Badge>
    </div>
    <Button
      type="button"
      variant="secondary"
      className="agency-form-save-draft-button gap-2 bg-zinc-800 text-white hover:bg-zinc-700 dark:bg-zinc-200 dark:text-zinc-900 dark:hover:bg-zinc-300"
      onClick={onSaveDraft}
      disabled={isLoading || isSaveDisabled}
    >
      {isLoading && <Loader2Icon className="size-4 animate-spin" />}
      Save Draft
    </Button>
    {isPublishDropdownVisible(visibility) && (
      <PublishDropdown
        visibility={visibility}
        isLoading={isLoading}
        onPublishToOrganization={onPublishToOrganization}
        onSubmitForReview={onSubmitForReview}
      />
    )}
  </div>
)

interface AgencyFormHeaderProps {
  visibility: AgencyVisibility
  version: number
  isLoading?: boolean
  isSaveDisabled?: boolean
  onBack: () => void
  onSaveDraft: () => void
  onPublishToOrganization: () => void
  onSubmitForReview: () => void
  className?: string
}

export const AgencyFormHeader = ({
  visibility,
  version,
  isLoading = false,
  isSaveDisabled = false,
  onBack,
  onSaveDraft,
  onPublishToOrganization,
  onSubmitForReview,
  className,
}: AgencyFormHeaderProps): ReactNode => (
  <div
    className={cn(
      'agency-form-header flex h-12 items-center justify-between gap-4 bg-muted px-4',
      className
    )}
  >
    <div className="agency-form-header-left flex items-center gap-3">
      <Button
        variant="ghost"
        size="sm"
        className="agency-form-back-button -ml-2 gap-2"
        onClick={onBack}
        disabled={isLoading}
      >
        <ArrowLeftIcon className="size-4" />
        <span className="hidden sm:inline">Back to agencies</span>
      </Button>
    </div>
    <AgencyFormHeaderActions
      visibility={visibility}
      version={version}
      isLoading={isLoading}
      isSaveDisabled={isSaveDisabled}
      onSaveDraft={onSaveDraft}
      onPublishToOrganization={onPublishToOrganization}
      onSubmitForReview={onSubmitForReview}
    />
  </div>
)

AgencyFormHeader.displayName = 'AgencyFormHeader'
